"use client";
import { SxProps, Theme } from "@mui/system";
import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";


export interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | null;
  helperText?: string;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * PhoneInput component using MUI Tel Input
 * - Default country: Romania (+40)
 * - onChange passes full value with country code
 */
export default function PhoneInput({
  value,
  onChange,
  label = "Număr de telefon",
  required = false,
  disabled = false,
  error = null,
  helperText,
  fullWidth = true,
  sx = {},
}: PhoneInputProps) {
  const handleChange = (newValue: string, info: MuiTelInputInfo) => {
    onChange(newValue);
  };

  return (
    <MuiTelInput
      value={value}
      onChange={handleChange}
      defaultCountry="RO"
      preferredCountries={["RO", "ES", "IT", "FR", "DE"]}
      langOfCountryName="ro"
      focusOnSelectCountry
      label={label}
      required={required}
      disabled={disabled}
      error={!!error}
      helperText={error || helperText}
      fullWidth={fullWidth}
      sx={sx}
    />
  );
}

/**
 * Utility function to extract phone digits from stored format
 * Removes country code prefix and returns digits only
 */
function extractPhoneDigits(phone: string | null | undefined): string {
  if (!phone) return "";

  let phoneDigits = phone.replace(/\D/g, "");
  // Remove country code if present (40)
  if (phoneDigits.startsWith("40")) {
    phoneDigits = phoneDigits.slice(2);
  }
  // Remove leading 0 if present
  if (phoneDigits.startsWith("0")) {
    phoneDigits = phoneDigits.slice(1);
  }
  return phoneDigits;
}

/**
 * Utility function to validate Romanian phone number
 * Accepts value with or without +40 prefix
 * Returns true if valid (9 digits after removing prefix), false otherwise
 */
export function validatePhone(phone: string | null | undefined): boolean {
  if (!phone || phone.trim() === "") {
    return true; // Empty is valid (for optional fields)
  }
  const digits = extractPhoneDigits(phone);
  return digits.length === 9;
}
